import React, { useRef } from "react";
import {
  Chart as ChartJS,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement,
  Filler,
  ScriptableContext,
  Tooltip,
  ChartData,
  ChartOptions,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  LinearScale,
  LineElement,
  CategoryScale,
  PointElement,
  Filler,
  Tooltip,
);

interface HourHourLineGraphProps {
  values: number[];
  startDate: Date;
  minY: number;
  getTooltipLabel?: (index: number) => string;
}

const formatDate = (date: Date): string =>
  date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  });

const HourLineGraph = ({
  values,
  startDate,
  minY,
  getTooltipLabel,
}: HourHourLineGraphProps) => {
  const graphRef = useRef();
  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { display: false },
      y: {
        display: false,
        max: Math.round(Math.max(...values) / 0.8),
        min: minY,
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    datasets: {
      line: {
        borderWidth: 1,
        pointHitRadius: 5,
      },
    },
    plugins: {
      tooltip: {
        enabled: getTooltipLabel !== undefined,
        callbacks: {
          ...(getTooltipLabel && {
            label: (tooltipItem) => getTooltipLabel(tooltipItem.dataIndex),
          }),
          title: (tooltipItems) => {
            const newDate = new Date(startDate);
            newDate.setHours(startDate.getHours() + tooltipItems[0].dataIndex);
            return formatDate(newDate);
          },
        },
      },
      legend: {
        display: false,
      },
    },
  };

  const data: ChartData<"line", number[], string> = {
    labels: values.map(() => ""), // labels not shown
    datasets: [
      {
        label: "Dataset",
        data: values,
        borderColor: "rgb(81, 118, 255)",
        backgroundColor: (context: ScriptableContext<"line">) => {
          const ctx = context.chart.ctx;
          if (!context.chart.chartArea) {
            return;
          }
          const gradient = ctx.createLinearGradient(
            0,
            context.chart.chartArea.top,
            0,
            context.chart.chartArea.bottom,
          );
          gradient.addColorStop(0, "rgba(56,164,248,1)");
          gradient.addColorStop(1, "rgba(168,217,255,0.5)");
          return gradient;
        },
        fill: "start",
      },
    ],
  };

  return (
    <Line
      ref={graphRef}
      width="100%"
      height="100%"
      options={options}
      data={data}
    />
  );
};

export default HourLineGraph;
